import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { MedalRewardIcon } from '../../../assets/svg/SVGComponents';
import { IBoardPresentationRoom } from '../../../dtos/IBoardSnapshot';
import { formatWallClockTime } from '../../../utils/scheduleDate';
import { categoryLabel } from '../logic/labels';
import { formatRemaining, presentationRooms } from '../logic/presentations';
import { IconText, LiveDot, Panel, SlotRow, SlotTime } from '../styles';

interface IPresentationPanelProps {
  rooms: IBoardPresentationRoom[] | undefined;
  /** Relógio em hora de parede (UTC), a escala do cronograma. */
  nowWall: number;
}

const MotionSlotRow = motion.create(SlotRow);

/**
 * "Apresentação técnica" no telão (FE-60): por sala, quem está com a banca e
 * quanto falta dos 15 min, e quem vem a seguir. Some quando não há nada.
 */
const PresentationPanel: React.FC<IPresentationPanelProps> = ({
  rooms,
  nowWall,
}) => {
  const reduce = useReducedMotion();
  const views = presentationRooms(rooms, nowWall);
  if (views.length === 0) return null;

  const showRoomName = (rooms?.length ?? 0) > 1;

  return (
    <Panel>
      <h2>
        <IconText>
          <MedalRewardIcon />
          Apresentação técnica
        </IconText>
      </h2>

      {views.map(view => (
        <React.Fragment key={view.room_id}>
          <AnimatePresence initial={false} mode="popLayout">
            {view.current && (
              <MotionSlotRow
                key={view.current.slot_id}
                $ongoing
                initial={reduce ? { opacity: 0 } : { opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, x: -40 }}
                transition={{ type: 'spring', bounce: 0, duration: 0.5 }}
              >
                <SlotTime>
                  <IconText>
                    <LiveDot />
                    {formatRemaining(view.remaining_seconds ?? 0)}
                  </IconText>
                </SlotTime>
                <strong>{view.current.team_name}</strong>
                <small style={{ gridColumn: 2 }}>
                  {[
                    showRoomName ? view.room_name : null,
                    categoryLabel(view.current.category),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </small>
              </MotionSlotRow>
            )}
            {view.next.map(next => (
              <MotionSlotRow
                key={next.slot_id}
                initial={reduce ? { opacity: 0 } : { opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, x: -40 }}
                transition={{ type: 'spring', bounce: 0, duration: 0.5 }}
              >
                <SlotTime>{formatWallClockTime(next.starts_at)}</SlotTime>
                <strong>{next.team_name}</strong>
                <small style={{ gridColumn: 2 }}>
                  {[
                    showRoomName ? view.room_name : null,
                    categoryLabel(next.category),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </small>
              </MotionSlotRow>
            ))}
          </AnimatePresence>
        </React.Fragment>
      ))}
    </Panel>
  );
};

export default PresentationPanel;
